import { MutableRefObject, useMemo } from 'react';
import Handsontable from 'handsontable/base';
import { Settings } from 'handsontable/plugins/contextMenu/contextMenu';

// Types
import { TranslationFunction } from '../../../components/i18n/types';
import { TextAlignment, VerticalAlignment } from '../../../../common/table/CellTypeConstants';
import { isGlobalDebugEnabled } from '../../../debug/debugUtil';
import { debugTableCell, debugTableInstance } from '../utils/tableDebugUtils';
import { TableOperations } from './useTableOperations';

const SEPARATOR = '---------';

interface UseTableContextMenuProps {
    elementId: string;
    tableOperationsRef: MutableRefObject<TableOperations>;
    hotTableInstanceRef: MutableRefObject<Handsontable.Core | null>;
    translateTableContextMenu: TranslationFunction;
}

const useTableContextMenu = ({
    elementId,
    hotTableInstanceRef,
    tableOperationsRef,
    translateTableContextMenu,
}: UseTableContextMenuProps) => {
    const getTableContextMenuConfig = useMemo((): Settings => {
        return {
            items: {
                cut: {
                    name: () => translateTableContextMenu('cut', 'Cut'),
                },
                copy: {
                    name: () => translateTableContextMenu('copy', 'Copy'),
                },
                paste: {
                    key: 'paste',
                    name: () => translateTableContextMenu('paste', 'Paste'),
                    callback() {
                        navigator.clipboard.readText().then((clipboard) => {
                            const copyPastePlugin =
                                hotTableInstanceRef.current &&
                                hotTableInstanceRef.current.getPlugin<'copyPaste'>('copyPaste');
                            copyPastePlugin?.paste(clipboard);
                        });
                    },
                },
                separator4: { name: SEPARATOR },
                add_rows_above_custom: {
                    name: () => tableOperationsRef.current.addRowsAboveMenuName(),
                    disabled: () => tableOperationsRef.current.addRowsAboveDisabled(),
                    callback: () => tableOperationsRef.current.addRowsAbove(),
                },
                add_rows_below_custom: {
                    name: () => tableOperationsRef.current.addRowsBelowMenuName(),
                    disabled: () => tableOperationsRef.current.addRowsBelowDisabled(),
                    callback: () => tableOperationsRef.current.addRowsBelow(),
                },
                separator1: { name: SEPARATOR },
                add_cols_left_custom: {
                    name: () => tableOperationsRef.current.addColsLeftMenuName(),
                    disabled: () => tableOperationsRef.current.addColsLeftDisabled(),
                    callback: () => tableOperationsRef.current.addColsLeft(),
                },
                add_cols_right_custom: {
                    name: () => tableOperationsRef.current.addColsRightMenuName(),
                    disabled: () => tableOperationsRef.current.addColsRightDisabled(),
                    callback: () => tableOperationsRef.current.addColsRight(),
                },
                separator2: { name: SEPARATOR },
                remove_rows_custom: {
                    name: () => tableOperationsRef.current.removeRowsMenuName(),
                    disabled: () => tableOperationsRef.current.removeRowsDisabled(),
                    callback: () => tableOperationsRef.current.removeRows(),
                },
                remove_cols_custom: {
                    name: () => tableOperationsRef.current.removeColsMenuName(),
                    disabled: () => tableOperationsRef.current.removeColsDisabled(),
                    callback: () => tableOperationsRef.current.removeCols(),
                },
                separator3: { name: SEPARATOR },
                custom_alignment: {
                    name: () => translateTableContextMenu('changeAlignment', 'Change Alignment...'),
                    submenu: {
                        items: [
                            {
                                key: 'custom_alignment:left',
                                name: () => tableOperationsRef.current.textAlignmentMenuName(TextAlignment.LEFT)(),
                                callback: () =>
                                    tableOperationsRef.current.updateCellTextAlignment(TextAlignment.LEFT)(),
                            },
                            {
                                key: 'custom_alignment:center',
                                name: () => tableOperationsRef.current.textAlignmentMenuName(TextAlignment.CENTER)(),
                                callback: () =>
                                    tableOperationsRef.current.updateCellTextAlignment(TextAlignment.CENTER)(),
                            },
                            {
                                key: 'custom_alignment:right',
                                name: () => tableOperationsRef.current.textAlignmentMenuName(TextAlignment.RIGHT)(),
                                callback: () =>
                                    tableOperationsRef.current.updateCellTextAlignment(TextAlignment.RIGHT)(),
                            },
                            { key: 'separator', name: SEPARATOR },
                            {
                                key: 'custom_alignment:top',
                                name: () =>
                                    tableOperationsRef.current.verticalAlignmentMenuName(VerticalAlignment.TOP)(),
                                callback: () =>
                                    tableOperationsRef.current.updateCellVerticalAlignment(VerticalAlignment.TOP)(),
                            },
                            {
                                key: 'custom_alignment:middle',
                                name: () =>
                                    tableOperationsRef.current.verticalAlignmentMenuName(VerticalAlignment.MIDDLE)(),
                                callback: () =>
                                    tableOperationsRef.current.updateCellVerticalAlignment(VerticalAlignment.MIDDLE)(),
                            },
                            {
                                key: 'custom_alignment:bottom',
                                name: () =>
                                    tableOperationsRef.current.verticalAlignmentMenuName(VerticalAlignment.BOTTOM)(),
                                callback: () =>
                                    tableOperationsRef.current.updateCellVerticalAlignment(VerticalAlignment.BOTTOM)(),
                            },
                        ],
                    },
                },
                ...(isGlobalDebugEnabled()
                    ? {
                          separator5: { name: SEPARATOR },

                          debugTableInstance: {
                              name: () => translateTableContextMenu('debugTableInstance', 'Debug Table Instance'),
                              callback: debugTableInstance,
                              hidden: !isGlobalDebugEnabled(),
                          },
                          debugCell: {
                              name: () => translateTableContextMenu('debugCell', 'Debug Cell'),
                              callback: debugTableCell(elementId),
                              hidden: !isGlobalDebugEnabled(),
                          },
                      }
                    : {}),
            },
        };
    }, [elementId, hotTableInstanceRef, translateTableContextMenu]);

    return {
        getTableContextMenuConfig,
    };
};

export default useTableContextMenu;
